import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { emailNotificationService } from '../../services/emailNotificationService'

// Async Thunks
export const fetchEmailNotifications = createAsyncThunk(
  'emailNotification/fetchEmailNotifications',
  async (params, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.getEmailNotifications(params)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch email notifications')
    }
  }
)

export const fetchEmailNotificationById = createAsyncThunk(
  'emailNotification/fetchEmailNotificationById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.getEmailNotificationById(id)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch email notification')
    }
  }
)

export const createEmailNotification = createAsyncThunk(
  'emailNotification/createEmailNotification',
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.createEmailNotification(notificationData)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create email notification')
    }
  }
)

export const updateEmailNotification = createAsyncThunk(
  'emailNotification/updateEmailNotification',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.updateEmailNotification(id, data)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update email notification')
    }
  }
)

export const deleteEmailNotification = createAsyncThunk(
  'emailNotification/deleteEmailNotification',
  async (id, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.deleteEmailNotification(id)

      // Since your API returns success: true, just return it
      // The unwrap() will succeed because no rejectWithValue was called
      return { id, ...response }

    } catch (error) {
      // This catches errors thrown by the service (handleError)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete email notification'
      return rejectWithValue(errorMessage)
    }
  }
)

export const toggleEmailNotificationStatus = createAsyncThunk(
  'emailNotification/toggleEmailNotificationStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.toggleEmailNotificationStatus(id, status)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to toggle email notification status')
    }
  }
)

export const fetchEmailStatistics = createAsyncThunk(
  'emailNotification/fetchEmailStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.getEmailStatistics()
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch email statistics')
    }
  }
)

export const fetchEmailTypes = createAsyncThunk(
  'emailNotification/fetchEmailTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.getEmailTypes()
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch email types')
    }
  }
)

export const testEmailConfiguration = createAsyncThunk(
  'emailNotification/testEmailConfiguration',
  async (email, { rejectWithValue }) => {
    try {
      const response = await emailNotificationService.testEmailConfiguration(email)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to test email configuration')
    }
  }
)

// Initial State
const initialState = {
  notifications: [],
  currentNotification: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  statistics: null,
  types: [],
  success: false
}

// Slice
const emailNotificationSlice = createSlice({
  name: 'emailNotification',
  initialState,
  reducers: {
    clearCurrentNotification: (state) => {
      state.currentNotification = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = false
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Fetch Email Notifications
      .addCase(fetchEmailNotifications.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(fetchEmailNotifications.fulfilled, (state, action) => {
        state.loading = false
        state.notifications = action.payload.notifications || []
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: action.payload.totalPages || 0
        }
      })
      .addCase(fetchEmailNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch Email Notification By ID
      .addCase(fetchEmailNotificationById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEmailNotificationById.fulfilled, (state, action) => {
        state.loading = false
        state.currentNotification = action.payload.notification
      })
      .addCase(fetchEmailNotificationById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Create Email Notification
      .addCase(createEmailNotification.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(createEmailNotification.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        // Add new notification to the beginning of the list
        state.notifications.unshift(action.payload.notification)
        state.pagination.total += 1
      })
      .addCase(createEmailNotification.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update Email Notification
      .addCase(updateEmailNotification.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(updateEmailNotification.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        // Update notification in the list
        const index = state.notifications.findIndex(n => n._id === action.payload.notification._id)
        if (index !== -1) {
          state.notifications[index] = action.payload.notification
        }
        // Update current notification if it's the one being edited
        if (state.currentNotification && state.currentNotification._id === action.payload.notification._id) {
          state.currentNotification = action.payload.notification
        }
      })
      .addCase(updateEmailNotification.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Delete Email Notification
      .addCase(deleteEmailNotification.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(deleteEmailNotification.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        // Remove notification from the list
        state.notifications = state.notifications.filter(n => n._id !== action.payload.id)
        state.pagination.total -= 1
        // Clear current notification if it's the one being deleted
        if (state.currentNotification && state.currentNotification._id === action.payload.id) {
          state.currentNotification = null
        }
      })
      .addCase(deleteEmailNotification.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Toggle Email Notification Status
      .addCase(toggleEmailNotificationStatus.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(toggleEmailNotificationStatus.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
        // Update notification status in the list
        const index = state.notifications.findIndex(n => n._id === action.payload.notification._id)
        if (index !== -1) {
          state.notifications[index] = action.payload.notification
        }
      })
      .addCase(toggleEmailNotificationStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch Email Statistics
      .addCase(fetchEmailStatistics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEmailStatistics.fulfilled, (state, action) => {
        state.loading = false
        state.statistics = action.payload.statistics
      })
      .addCase(fetchEmailStatistics.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch Email Types
      .addCase(fetchEmailTypes.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchEmailTypes.fulfilled, (state, action) => {
        state.loading = false
        state.types = action.payload.types || []
      })
      .addCase(fetchEmailTypes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Test Email Configuration
      .addCase(testEmailConfiguration.pending, (state) => {
        state.loading = true
        state.error = null
        state.success = false
      })
      .addCase(testEmailConfiguration.fulfilled, (state) => {
        state.loading = false
        state.success = true
      })
      .addCase(testEmailConfiguration.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  clearCurrentNotification,
  clearError,
  clearSuccess,
  resetState
} = emailNotificationSlice.actions

export default emailNotificationSlice.reducer